import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Heart, FileText, Home as HomeIcon, Users, ClipboardList, ShieldCheck,
  Phone, MessageSquare, Calendar, Sparkles, Mail, ArrowRight, BadgeCheck, Quote, Info,
  Coffee, NotebookPen, HandHelping,
  Truck, Wallet, ScrollText, Stethoscope, Laptop, Scale, FolderOpen, Flower2, Plane, ListChecks,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { DaisyMark } from "../Logo";
import BookingFlow from "../BookingFlow";
import { createEnquiry } from "../../lib/api";
import { SITE } from "../../lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1566741716102-6d46d7e6d859?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxwYXBlcndvcmslMjBvcmdhbmlzZWQlMjBkZXNrJTIwdGVhfGVufDB8fHx8MTc4MDE1MzExN3ww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.pexels.com/photos/3729182/pexels-photo-3729182.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SERVICE_IMG = "https://images.unsplash.com/photo-1616286608358-0e1b143f7d2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjB0YWxraW5nJTIwdG8lMjBzbWlsaW5nJTIwd29tYW58ZW58MHx8fHwxNzgwMTUzMTE3fDA&ixlib=rb-4.1.0&q=85";
const PAPERS_IMG = "https://images.pexels.com/photos/7681218/pexels-photo-7681218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const BookButton = ({ children = "Book a Free Introductory Call", variant = "primary", testId = "cta-book", to = "/contact#booking" }) => (
  <Link
    to={to}
    className={variant === "secondary" ? "btn-secondary" : "btn-primary"}
    data-testid={testId}
  >
    <Calendar className="w-5 h-5" /> {children}
  </Link>
);

const Section = ({ id, className = "", children, testId }) => (
  <section id={id} data-testid={testId || id} className={`py-16 sm:py-24 ${className}`}>
    <div className="container-soft relative">{children}</div>
  </section>
);

const SubtleDaisy = ({ className = "" }) => (
  <div className={`pointer-events-none select-none opacity-25 ${className}`} aria-hidden>
    <DaisyMark size={120} />
  </div>
);

/* -------------------------------- HERO -------------------------------- */
export const Hero = () => (
  <section id="hero" className="relative pt-12 pb-20 sm:pt-20 sm:pb-28 overflow-hidden" data-testid="section-hero">
    <SubtleDaisy className="absolute -top-8 -right-10 hidden md:block" />
    <div className="container-soft grid lg:grid-cols-2 gap-12 items-center relative">
      <div className="reveal">
        <span className="chip" data-testid="hero-chip"><Sparkles className="w-4 h-4" /> Practical help, calmly done</span>
        <h1 className="section-title mt-5 text-forest">
          Daisy Does the things you do not have time for,{" "}
          <span className="relative inline-block">
            <span className="relative z-10">or do not want to do alone.</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-daisy/70 -z-0 rounded" />
          </span>
        </h1>
        <p className="section-sub mt-6">
          Friendly, practical support with household admin, benefit applications, forms, appointments and everyday tasks.
        </p>
        <p className="mt-3 text-lg text-charcoal/80 max-w-2xl">
          Sarah helps elderly clients, families and busy households feel more on top of things, less overwhelmed and back in control.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <BookButton testId="hero-book-button" />
          <Link to="/services" className="btn-ghost" data-testid="hero-services-button">
            View Services <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <p className="mt-5 text-base text-forest/70 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-forest" />
          You do not need to have everything ready before you call.
        </p>
      </div>

      <div className="relative">
        <div className="yellow-square w-2/3 h-2/3 -top-6 -left-6" />
        <img
          src={HERO_IMG}
          alt="A calm desk with tea, paperwork and a notebook"
          className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]"
          data-testid="hero-image"
        />
        <div className="absolute -bottom-6 -right-2 sm:-right-6 soft-card px-5 py-4 max-w-xs hidden sm:flex items-start gap-3">
          <Heart className="w-7 h-7 text-forest shrink-0" />
          <p className="text-base text-charcoal">A short conversation is usually the easiest place to start.</p>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------- TRUST STRIP -------------------------------- */
export const TrustStrip = () => {
  const items = [
    { icon: HandHelping, label: "Friendly support" },
    { icon: ClipboardList, label: "Clear admin help" },
    { icon: Info, label: "Plain-English explanations" },
    { icon: ShieldCheck, label: "Respectful and discreet" },
    { icon: Users, label: "Help for families and older people" },
  ];
  return (
    <section className="py-10 bg-sage/30 border-y border-forest/10" data-testid="section-trust-strip">
      <div className="container-soft grid grid-cols-2 md:grid-cols-5 gap-4">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-3 text-forest">
            <it.icon className="w-6 h-6 shrink-0" strokeWidth={1.8} />
            <span className="font-semibold text-base sm:text-lg">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -------------------------------- GUIDED CHOICE -------------------------------- */
export const GuidedChoice = () => {
  const opts = [
    {
      title: "I need help with a benefit application",
      text: "Sarah can help pull the information together, complete forms and explain the next step.",
      cta: "Book a Benefits Help Call",
      to: "/benefit-application-help",
      icon: FileText,
    },
    {
      title: "I need help for an elderly parent or relative",
      text: "Support for families who are trying to help someone they care about.",
      cta: "Speak to Sarah",
      to: "/elderly-support",
      icon: Heart,
    },
    {
      title: "I am overwhelmed by household admin",
      text: "Help with letters, bills, emails, appointments, documents and general paperwork.",
      cta: "Get Admin Help",
      to: "/services",
      icon: HomeIcon,
    },
    {
      title: "I have a form or letter I do not understand",
      text: "Sarah can help you understand what is needed and get it sorted.",
      cta: "Ask Sarah",
      to: "/contact#booking",
      icon: NotebookPen,
    },
  ];
  return (
    <Section id="guided-choice" testId="section-guided-choice">
      <div className="max-w-3xl">
        <h2 className="section-title">What do you need help with?</h2>
        <p className="section-sub mt-4">Choose the option that sounds closest to your situation.</p>
      </div>
      <div className="mt-10 grid sm:grid-cols-2 gap-6">
        {opts.map((o, i) => (
          <Link
            to={o.to}
            key={o.title}
            data-testid={`guided-card-${i}`}
            className="soft-card group p-7 sm:p-8 hover:-translate-y-0.5 transition-transform relative overflow-hidden"
          >
            <div className="yellow-square w-20 h-20 -top-4 -right-4 opacity-90" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 grid place-items-center mb-4">
                <o.icon className="w-7 h-7 text-forest" strokeWidth={1.8} />
              </div>
              <h3 className="font-serif text-2xl text-forest">{o.title}</h3>
              <p className="mt-3 text-charcoal/85 text-base sm:text-lg">{o.text}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-semibold text-forest group-hover:gap-3 transition-all">
                {o.cta} <ArrowRight className="w-5 h-5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
};

/* -------------------------------- SERVICES OVERVIEW -------------------------------- */
export const ServicesOverview = () => {
  const items = [
    { title: "Benefit Application Support", text: "Help with forms, information gathering, supporting documents and application admin.", icon: FileText },
    { title: "Household Admin", text: "Letters, bills, emails, appointments, online accounts and paperwork.", icon: ClipboardList },
    { title: "Support for Elderly Clients", text: "Patient, respectful support for older people who need an extra pair of hands.", icon: Heart },
    { title: "Family Support", text: "Helping families support parents, grandparents and vulnerable relatives.", icon: Users },
    { title: "Home Services & Everyday Help", text: "Arranging appointments, sorting the to-do list and helping things move forward.", icon: HomeIcon },
    { title: "Applications & Forms", text: "Online and paper forms, renewals, registrations and official letters.", icon: NotebookPen },
  ];
  return (
    <Section id="services" testId="section-services-overview" className="bg-white">
      <div className="grid md:grid-cols-[1fr_auto] md:items-end gap-6 mb-12">
        <div>
          <h2 className="section-title">What Daisy Does</h2>
          <p className="section-sub mt-4">Sarah helps with the everyday admin and practical tasks that can quickly become stressful.</p>
        </div>
        <Link to="/services" className="btn-ghost h-12 px-5 text-base" data-testid="services-view-all">
          View all services <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((s, i) => (
          <article key={s.title} className="soft-card p-7 hover:-translate-y-0.5 transition-transform" data-testid={`service-card-${i}`}>
            <div className="w-12 h-12 rounded-xl bg-daisy/40 grid place-items-center mb-4">
              <s.icon className="w-6 h-6 text-forest" strokeWidth={1.8} />
            </div>
            <h3 className="font-serif text-2xl text-forest">{s.title}</h3>
            <p className="mt-3 text-charcoal/85">{s.text}</p>
          </article>
        ))}
      </div>
      <div className="mt-10 text-center">
        <p className="text-lg text-charcoal/85 mb-4">Not sure what you need? Book a free call with Sarah.</p>
        <BookButton testId="services-overview-book" />
      </div>
    </Section>
  );
};

/* -------------------------------- MORE WAYS SARAH HELPS -------------------------------- */
const ADDITIONAL_SERVICES_DATA = [
  { title: "Letter Writing & Complaints", icon: ScrollText,
    text: "Calm, clear letters to councils, energy companies, the NHS, insurers and providers — including formal complaints when something has gone wrong." },
  { title: "Moving Home Admin", icon: Truck,
    text: "Change of address, council tax, utilities, GP and dentist registration, insurance updates and the rest of the move-day list." },
  { title: "Bereavement Admin Support", icon: Flower2,
    text: "Gentle, practical help after a loss — Tell Us Once, notifying providers, closing accounts and gathering paperwork. Always at your pace." },
  { title: "Power of Attorney Paperwork", icon: Scale,
    text: "Help gathering the information and completing the LPA forms (administrative support only — we will signpost to a solicitor for legal advice)." },
  { title: "Hospital & Medical Admin", icon: Stethoscope,
    text: "Co-ordinating appointments, chasing prescriptions, following up referrals and keeping a clear record of what was said and when." },
  { title: "Online Accounts & Tech Help", icon: Laptop,
    text: "Setting up and managing online accounts — Government Gateway, NHS app, energy portals, banking — explained at a pace that suits you." },
  { title: "Bills & Subscriptions Audit", icon: Wallet,
    text: "A friendly tidy-up of your bills and subscriptions — cancelling what you don't use and finding savings on what you do." },
  { title: "Paperwork Declutter", icon: FolderOpen,
    text: "Sorting through years of accumulated letters, statements and folders — keeping what matters and confidentially shredding the rest." },
  { title: "Travel & Passport Admin", icon: Plane,
    text: "Passport renewals, visa application paperwork, travel insurance forms and the bits of admin that come with going away." },
];

const AdditionalServiceCard = ({ service, index }) => (
  <article
    className="soft-card p-7 hover:-translate-y-0.5 transition-transform relative overflow-hidden"
    data-testid={`additional-service-${index}`}
  >
    <div className="yellow-square w-16 h-16 -top-3 -right-3 opacity-90" />
    <div className="relative">
      <div className="w-12 h-12 rounded-xl bg-forest/10 grid place-items-center mb-4">
        <service.icon className="w-6 h-6 text-forest" strokeWidth={1.8} />
      </div>
      <h3 className="font-serif text-2xl text-forest">{service.title}</h3>
      <p className="mt-3 text-charcoal/85">{service.text}</p>
    </div>
  </article>
);

export const AdditionalServices = () => (
  <Section id="more-services" testId="section-additional-services">
    <div className="max-w-3xl">
      <span className="chip"><Sparkles className="w-4 h-4" /> More ways Sarah helps</span>
      <h2 className="section-title mt-4">If admin is involved, Sarah can probably help.</h2>
      <p className="section-sub mt-4">
        Beyond the everyday admin and benefit applications, Sarah quietly takes care of the bits of life that always end up
        sitting on the kitchen table for too long.
      </p>
    </div>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {ADDITIONAL_SERVICES_DATA.map((service, index) => (
        <AdditionalServiceCard key={service.title} service={service} index={index} />
      ))}
    </div>
    <div className="mt-10 soft-card p-6 sm:p-8 bg-sage/30 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="additional-services-cta">
      <ListChecks className="w-7 h-7 text-forest shrink-0" />
      <p className="flex-1 text-lg text-charcoal">
        Have something on your list that isn't shown here? Most life admin follows the same pattern — just ask Sarah.
      </p>
      <BookButton testId="additional-services-book">Ask Sarah</BookButton>
    </div>
  </Section>
);

/* -------------------------------- BENEFIT SUPPORT -------------------------------- */
export const BenefitSupport = () => {
  const list = [
    "Attendance Allowance",
    "Pension Credit",
    "Blue Badge applications",
    "Council Tax Reduction",
    "PIP-related paperwork where appropriate",
    "General benefit and local authority forms",
  ];
  return (
    <Section id="benefit-support" testId="section-benefit-support">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="yellow-square w-3/5 h-3/5 -bottom-6 -right-6" />
          <img src={PAPERS_IMG} alt="Paperwork being neatly sorted" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" data-testid="benefit-image" />
        </div>
        <div>
          <span className="chip"><FileText className="w-4 h-4" /> Benefit application help</span>
          <h2 className="section-title mt-4">Benefit applications without the stress</h2>
          <p className="section-sub mt-4">
            Forms can feel confusing, especially when they are long, repetitive or online. Daisy Does helps gather the
            information, complete forms carefully, prepare supporting documents and explain the next steps clearly.
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {list.map((item) => (
              <li key={item} className="flex items-start gap-2 text-charcoal">
                <BadgeCheck className="w-5 h-5 text-forest mt-1 shrink-0" />
                <span className="text-base">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-wrap gap-3">
            <BookButton testId="benefit-cta-book">Get Help With an Application</BookButton>
            <Link to="/benefit-application-help" className="btn-ghost" data-testid="benefit-learn-more">Learn more <ArrowRight className="w-5 h-5" /></Link>
          </div>
          <p className="mt-5 text-base text-forest/70">
            Sarah cannot guarantee an outcome, but she can help make the process clearer and more manageable.
          </p>
        </div>
      </div>
    </Section>
  );
};

/* -------------------------------- BOOKING SECTION -------------------------------- */
export const BookingSection = () => (
  <Section id="booking" testId="section-booking" className="bg-sage/20">
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
      <div>
        <span className="chip"><Calendar className="w-4 h-4" /> Free 15-minute call</span>
        <h2 className="section-title mt-4">Book a free introductory call</h2>
        <p className="section-sub mt-4">
          Choose a time to speak with Sarah. The call is free, friendly and simply helps her understand what you need.
        </p>
        <ul className="mt-6 space-y-3">
          {["No pressure", "No obligation", "For you or someone you are helping", "You do not need to have everything ready", "Sarah will explain the next steps clearly"].map((r) => (
            <li key={r} className="flex items-start gap-2 text-charcoal">
              <CheckCircle2 className="w-5 h-5 text-forest mt-1 shrink-0" /> <span>{r}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 soft-card p-5 flex items-start gap-3">
          <Phone className="w-6 h-6 text-forest shrink-0" />
          <div>
            <p className="font-semibold text-forest">Prefer to talk now?</p>
            <a href={SITE.tel} className="text-lg text-forest underline" data-testid="booking-section-call">{SITE.phoneDisplay}</a>
          </div>
        </div>
        <InlineTestimonial index={0} className="mt-6" />
      </div>
      <BookingFlow />
    </div>
  </Section>
);

/* -------------------------------- ABOUT SARAH -------------------------------- */
export const AboutSarah = () => (
  <Section id="about-sarah" testId="section-about-sarah" className="bg-white">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="relative order-2 lg:order-1">
        <div className="yellow-square w-1/2 h-3/5 -top-4 -left-4" />
        <img src={ABOUT_IMG} alt="A warm, friendly conversation over coffee" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" data-testid="about-image" />
      </div>
      <div className="order-1 lg:order-2">
        <span className="chip"><Coffee className="w-4 h-4" /> About Sarah</span>
        <h2 className="section-title mt-4">Hi, I'm Sarah — the person behind Daisy Does.</h2>
        <p className="mt-5 text-lg text-charcoal/85">
          Daisy Does was created to give people calm, practical help with the things that can quickly become stressful:
          forms, applications, household admin, online systems, appointments and everyday sorting.
        </p>
        <p className="mt-4 text-lg text-charcoal/85">
          Whether you are helping an elderly parent, trying to understand an application, or simply need someone steady to
          get things done, Sarah provides friendly, patient and reliable support.
        </p>
        <p className="mt-4 text-lg text-forest font-semibold">
          Everything is handled with care, discretion and respect.
        </p>
        <div className="mt-7"><BookButton testId="about-book">Speak to Sarah</BookButton></div>
      </div>
    </div>
  </Section>
);

/* -------------------------------- HOW IT WORKS -------------------------------- */
export const HowItWorks = () => {
  const steps = [
    { title: "Tell Sarah what you need help with", text: "Start with a quick call or message.", icon: MessageSquare },
    { title: "Sarah reviews what is required", text: "She will explain what information or documents are needed.", icon: ClipboardList },
    { title: "The admin gets done properly", text: "Forms, calls, applications or household tasks are handled carefully.", icon: FileText },
    { title: "You feel back in control", text: "Everything is explained clearly, with next steps set out.", icon: CheckCircle2 },
  ];
  return (
    <Section id="how-it-works" testId="section-how-it-works">
      <h2 className="section-title">How it works</h2>
      <p className="section-sub mt-4">Four simple steps. No jargon, no surprises.</p>
      <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((s, i) => (
          <li key={s.title} className="soft-card p-7 relative" data-testid={`how-step-${i}`}>
            <span className="absolute -top-4 left-7 w-9 h-9 rounded-full bg-daisy grid place-items-center font-bold text-forest">
              {i + 1}
            </span>
            <s.icon className="w-7 h-7 text-forest mb-3" strokeWidth={1.8} />
            <h3 className="font-serif text-xl text-forest">{s.title}</h3>
            <p className="mt-2 text-charcoal/85">{s.text}</p>
          </li>
        ))}
      </ol>
      <div className="mt-10"><BookButton testId="how-it-works-book">Start With a Free Call</BookButton></div>
    </Section>
  );
};

/* -------------------------------- WHY CHOOSE -------------------------------- */
export const WhyChoose = () => {
  const items = [
    "Reliable and methodical",
    "Patient and friendly",
    "Clear, plain-English support",
    "Respectful of elderly and vulnerable clients",
    "Confidential handling of documents",
    "Flexible support for families and households",
    "Local, personal service rather than a call centre",
    "Practical help that makes things feel manageable",
  ];
  return (
    <Section id="why-choose" testId="section-why-choose" className="bg-sage/25">
      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div>
          <h2 className="section-title">Why people choose Daisy Does</h2>
          <p className="section-sub mt-4">
            Calm, capable, kind — admin help that feels like a friend who knows what they're doing.
          </p>
          <div className="mt-7"><BookButton testId="why-choose-book" /></div>
        </div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {items.map((it, i) => (
            <li key={it} className="flex items-start gap-3 bg-cream rounded-2xl p-4" data-testid={`why-item-${i}`}>
              <CheckCircle2 className="w-5 h-5 text-forest mt-1 shrink-0" />
              <span className="text-base sm:text-lg">{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
};

/* -------------------------------- PRICING PREVIEW -------------------------------- */
export const PricingPreview = () => {
  const cards = [
    { title: "Introductory Call", price: "Free", description: "A 15-minute call to understand what help is needed.", highlight: true },
    { title: "Hourly Support", price: "£45 / hour", description: "For admin, applications and household support." },
    { title: "Application Support", price: "Fixed fee by agreement", description: "For defined forms or application support." },
    { title: "Home Admin Sessions", price: "Half-day or full-day", description: "For bigger admin tasks or household sorting." },
  ];
  return (
    <Section id="pricing-preview" testId="section-pricing-preview">
      <h2 className="section-title">Simple support, priced clearly</h2>
      <p className="section-sub mt-4">Start with a free call. Sarah will then explain the best option for what you need.</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c, i) => (
          <div
            key={c.title}
            data-testid={`pricing-card-${i}`}
            className={`soft-card p-7 relative ${c.highlight ? "ring-2 ring-daisy" : ""}`}
          >
            {c.highlight && (
              <span className="absolute -top-3 left-7 chip bg-daisy text-forest" data-testid="pricing-best">Best place to start</span>
            )}
            <h3 className="font-serif text-2xl text-forest">{c.title}</h3>
            <p className="mt-3 text-3xl font-bold text-forest">{c.price}</p>
            <p className="mt-3 text-charcoal/85">{c.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <BookButton testId="pricing-preview-book">Book a Free Call to Discuss Pricing</BookButton>
        <Link to="/pricing" className="btn-ghost" data-testid="pricing-preview-link">See full pricing <ArrowRight className="w-5 h-5" /></Link>
      </div>
    </Section>
  );
};

/* -------------------------------- CONTACT OPTIONS -------------------------------- */
export const ContactOptions = () => {
  const opts = [
    { title: "Book a call", text: "Pick a time that works for you.", cta: "Book a Free Call", to: "/contact#booking", icon: Calendar },
    { title: "Send a message", text: "Tell Sarah what you need help with.", cta: "Send Enquiry", to: "/contact#enquiry", icon: MessageSquare },
    { title: "Call directly", text: "Speak to Sarah if you prefer to talk.", cta: "Call Sarah", to: SITE.tel, icon: Phone, external: true },
    { title: "Ask for help for someone else", text: "Family members can contact Sarah on behalf of a parent or relative.", cta: "Get Support", to: "/contact#booking", icon: Users },
  ];
  return (
    <Section id="contact-options" testId="section-contact-options" className="bg-white">
      <h2 className="section-title">Choose the easiest way to contact Sarah</h2>
      <p className="section-sub mt-4">Whichever feels easiest — Sarah will do the rest.</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {opts.map((o, i) => (
          <div key={o.title} className="soft-card p-7 flex flex-col" data-testid={`contact-option-${i}`}>
            <div className="w-12 h-12 rounded-xl bg-forest/10 grid place-items-center mb-4">
              <o.icon className="w-6 h-6 text-forest" strokeWidth={1.8} />
            </div>
            <h3 className="font-serif text-xl text-forest">{o.title}</h3>
            <p className="mt-2 text-charcoal/85 flex-1">{o.text}</p>
            {o.external ? (
              <a href={o.to} className="mt-5 btn-secondary" data-testid={`contact-opt-cta-${i}`}>{o.cta}</a>
            ) : (
              <Link to={o.to} className="mt-5 btn-secondary" data-testid={`contact-opt-cta-${i}`}>{o.cta}</Link>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
};

/* -------------------------------- TESTIMONIALS -------------------------------- */
export const TESTIMONIAL_DATA = [
  {
    quote:
      "Sarah helped me at a very stressful time when I was moving and trying to understand what benefits and support I might be able to apply for. She was calm, patient and explained everything clearly. She helped me get my paperwork organised and made the whole process feel much less overwhelming.",
    name: "Eileen",
    context: "Moving and benefit application support",
  },
  {
    quote:
      "I had been putting off dealing with my benefit paperwork because I did not know where to start. Sarah helped me understand what information was needed and worked through the forms with care and patience. It made a difficult job feel manageable.",
    name: "Paul",
    context: "Benefit application support",
  },
  {
    quote:
      "Sarah helped me get on top of household admin that had been building up for months. She was organised, practical and very easy to deal with. Having someone calm and capable to help sort everything made a real difference.",
    name: "Fiona",
    context: "Admin and home admin support",
  },
  {
    quote:
      "We contacted Sarah because we were trying to help my elderly mother with letters, appointments and paperwork. She was kind, discreet and very practical. She helped us understand what needed doing and gave us confidence that things were being handled properly.",
    name: "Margaret",
    context: "Support for an elderly relative",
  },
  {
    quote:
      "I find forms stressful, especially when they are online or ask for the same information in different ways. Sarah helped me work through everything step by step and explained it in plain English.",
    name: "Alan",
    context: "Forms and applications",
  },
  {
    quote:
      "I needed help sorting letters, bills and general household paperwork. Sarah did not make me feel embarrassed about being behind. She just got stuck in, helped me organise it and made the next steps clear.",
    name: "Christine",
    context: "Household paperwork",
  },
  {
    quote:
      "Sarah was patient, respectful and never rushed me. She helped me understand what the letters meant and what I needed to do next. It was a relief to have someone explain things clearly.",
    name: "David",
    context: "Admin support",
  },
  {
    quote:
      "With work and family life, I did not have time to keep chasing paperwork and appointments. Sarah helped take the pressure off and got things moving. It felt like having someone reliable in my corner.",
    name: "Rachel",
    context: "Family and household admin",
  },
  {
    quote:
      "I was worried about getting the forms wrong, but Sarah helped me understand what information was needed and kept everything calm and organised. I felt much more confident once she was involved.",
    name: "Jean",
    context: "Benefit paperwork support",
  },
  {
    quote:
      "Sarah helped me deal with a pile of admin I had been avoiding for weeks. She was practical, clear and very efficient. Everything felt easier once we had a plan.",
    name: "Martin",
    context: "General admin support",
  },
];

const TestimonialCard = ({ t, i }) => (
  <figure
    className="soft-card p-7 relative h-full flex flex-col"
    data-testid={`testimonial-${i}`}
  >
    <Quote className="absolute -top-3 left-6 w-8 h-8 text-daisy" aria-hidden />
    <blockquote className="font-serif text-lg sm:text-xl text-forest leading-snug flex-1">
      "{t.quote}"
    </blockquote>
    <figcaption className="mt-5 pt-5 border-t border-forest/10">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-daisy text-forest font-serif text-xl grid place-items-center shrink-0" aria-hidden>
          {t.name.charAt(0)}
        </span>
        <div>
          <p className="font-semibold text-forest">— {t.name}</p>
          <p className="text-sm text-charcoal/70">{t.context}</p>
        </div>
      </div>
    </figcaption>
  </figure>
);

export const Testimonials = () => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? TESTIMONIAL_DATA : TESTIMONIAL_DATA.slice(0, 6);
  return (
    <Section id="testimonials" testId="section-testimonials" className="bg-sage/15">
      <div className="max-w-3xl">
        <span className="chip"><Quote className="w-4 h-4" /> Kind words</span>
        <h2 className="section-title mt-4">Kind words from people Sarah has helped</h2>
        <p className="section-sub mt-4">
          Real feedback from real clients — shared with permission. First names only.
        </p>
      </div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {visible.map((t, i) => (
          <TestimonialCard key={t.name} t={t} i={i} />
        ))}
      </div>
      {!showAll && TESTIMONIAL_DATA.length > 6 && (
        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="btn-ghost h-12 px-6 text-base"
            data-testid="testimonials-show-more"
          >
            Read more kind words <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
      <div className="mt-10 soft-card p-6 sm:p-8 bg-cream flex flex-col sm:flex-row gap-4 sm:items-center">
        <Heart className="w-7 h-7 text-forest shrink-0" />
        <p className="flex-1 text-lg text-charcoal">
          If a short call would help you too, Sarah would love to hear from you.
        </p>
        <BookButton testId="testimonials-book" />
      </div>
    </Section>
  );
};

// Small inline quote — used near booking CTAs to do double duty as social proof.
export const InlineTestimonial = ({ index = 0, className = "" }) => {
  const t = TESTIMONIAL_DATA[index] || TESTIMONIAL_DATA[0];
  return (
    <figure
      className={`soft-card p-5 sm:p-6 bg-cream border-l-4 border-daisy ${className}`}
      data-testid={`inline-testimonial-${index}`}
    >
      <Quote className="w-6 h-6 text-daisy mb-2" aria-hidden />
      <blockquote className="font-serif text-lg text-forest leading-snug">
        "{t.quote}"
      </blockquote>
      <figcaption className="mt-3 text-sm text-charcoal/75">
        — <span className="font-semibold text-forest">{t.name}</span> · {t.context}
      </figcaption>
    </figure>
  );
};

/* -------------------------------- DISCLAIMER -------------------------------- */
export const Disclaimer = () => (
  <Section id="disclaimer" testId="section-disclaimer" className="py-12">
    <div className="soft-card p-7 sm:p-9 bg-cream border-2 border-daisy/50 flex items-start gap-4 max-w-4xl">
      <Info className="w-7 h-7 text-forest shrink-0 mt-1" />
      <div>
        <h3 className="font-serif text-2xl text-forest">Legal Advice Disclaimer</h3>
        <p className="mt-3 text-base text-charcoal/90">
          Daisy Does provides practical administrative support, organisation and assistance with forms, applications,
          paperwork and everyday admin tasks.
        </p>
        <p className="mt-3 text-base text-charcoal/90">
          Daisy Does does not provide legal advice, financial advice, tax advice, regulated benefits advice or any
          other regulated professional advice. Any information provided is general administrative support only and
          should not be relied upon as a substitute for advice from a qualified solicitor, financial adviser, tax
          adviser, benefits adviser or other appropriate professional.
        </p>
        <p className="mt-3 text-base text-charcoal/90">
          Where specialist advice is required, Sarah can help clients identify the type of professional or
          organisation they may need to contact, but Daisy Does cannot advise on legal rights, legal claims,
          financial decisions, tax matters, benefit entitlement or the likely outcome of any application.
        </p>
      </div>
    </div>
  </Section>
);

/* -------------------------------- FINAL CTA -------------------------------- */
export const FinalCTA = () => (
  <Section id="final-cta" testId="section-final-cta">
    <div className="relative soft-card p-10 sm:p-16 text-center bg-forest text-cream overflow-hidden">
      <div className="yellow-square w-40 h-40 -top-8 -left-8 opacity-90" />
      <div className="yellow-square w-24 h-24 -bottom-6 -right-6 opacity-90" />
      <div className="relative">
        <h2 className="font-serif text-4xl sm:text-5xl text-cream">Do not let paperwork, forms or household admin sit there causing stress.</h2>
        <p className="mt-5 text-lg text-cream/90 max-w-2xl mx-auto">
          Book a free call with Sarah and take the first simple step toward getting it sorted.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/contact#booking" className="btn-secondary" data-testid="final-cta-book">
            <Calendar className="w-5 h-5" /> Book a Free Introductory Call
          </Link>
          <a href={SITE.tel} className="btn-ghost text-cream border-cream hover:bg-cream hover:text-forest" data-testid="final-cta-call">
            <Phone className="w-5 h-5" /> Call {SITE.phoneDisplay}
          </a>
        </div>
        <p className="mt-5 text-cream/80">A short conversation is usually the easiest place to start.</p>

        <figure className="mt-10 mx-auto max-w-2xl bg-cream/10 border border-cream/20 rounded-2xl p-5 sm:p-6 text-left" data-testid="final-cta-testimonial">
          <Quote className="w-6 h-6 text-daisy mb-2" aria-hidden />
          <blockquote className="font-serif text-lg sm:text-xl text-cream leading-snug">
            "{TESTIMONIAL_DATA[3].quote}"
          </blockquote>
          <figcaption className="mt-3 text-sm text-cream/80">
            — <span className="font-semibold text-cream">{TESTIMONIAL_DATA[3].name}</span> · {TESTIMONIAL_DATA[3].context}
          </figcaption>
        </figure>
      </div>
    </div>
  </Section>
);

/* -------------------------------- CONTACT FORM -------------------------------- */
const ENQUIRY_INITIAL = Object.freeze({
  name: "", phone: "", email: "", help_with: "", for_whom: "self", preferred_contact: "email",
});

const ENQUIRY_FORWHOM = [
  { value: "self", label: "For me" },
  { value: "someone_else", label: "For someone I care for" },
];

const ENQUIRY_CONTACT = ["phone", "email", "either"];

const enquiryInputCls =
  "mt-1 w-full h-14 px-4 rounded-2xl border-2 border-forest/15 bg-white text-lg focus:outline-none focus:border-forest";

const isEnquiryComplete = (form) =>
  Boolean(form.name.trim() && /.+@.+\..+/.test(form.email) && form.help_with.trim());

const ContactFormIntro = () => (
  <div>
    <span className="chip"><Mail className="w-4 h-4" /> Get in touch</span>
    <h2 className="section-title mt-4">Send Sarah a quick message</h2>
    <p className="section-sub mt-4">Tell Sarah what you need help with and she will come back to you. Short messages are perfectly fine.</p>
    <ul className="mt-6 space-y-3 text-charcoal/85">
      <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-forest mt-1" />You can write on behalf of someone else.</li>
      <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-forest mt-1" />No jargon — plain English is welcome.</li>
      <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-forest mt-1" />Sarah will gently explain the next step.</li>
    </ul>
    <div className="mt-6 soft-card p-5 bg-cream flex items-center gap-3">
      <Phone className="w-6 h-6 text-forest" />
      <a href={SITE.tel} className="text-lg text-forest font-semibold underline" data-testid="contact-form-phone">{SITE.phoneDisplay}</a>
    </div>
  </div>
);

const EnquiryConfirmation = () => (
  <div className="relative text-center py-10">
    <div className="mx-auto w-14 h-14 rounded-full bg-daisy grid place-items-center mb-3">
      <CheckCircle2 className="w-8 h-8 text-forest" />
    </div>
    <h3 className="font-serif text-2xl text-forest">Message sent</h3>
    <p className="mt-2 text-charcoal/85">Sarah will be in touch soon. Thank you.</p>
  </div>
);

const EnquiryToggleGroup = ({ label, value, options, onChange, testIdPrefix, capitalize }) => (
  <div>
    <span className="font-semibold text-forest">{label}</span>
    <div className="mt-2 flex gap-2 flex-wrap">
      {options.map((opt) => {
        const optValue = typeof opt === "string" ? opt : opt.value;
        const optLabel = typeof opt === "string" ? opt : opt.label;
        const selected = value === optValue;
        return (
          <button type="button" key={optValue} onClick={() => onChange(optValue)}
            data-testid={`${testIdPrefix}-${optValue}`}
            className={`px-5 h-12 rounded-full font-semibold ${capitalize ? "capitalize" : ""} border-2 ${
              selected ? "bg-forest text-cream border-forest" : "bg-white text-forest border-forest/15"
            }`}>
            {optLabel}
          </button>
        );
      })}
    </div>
  </div>
);

const EnquiryFields = ({ form, setField, submitting }) => (
  <div className="relative grid gap-4">
    <label className="block">
      <span className="font-semibold text-forest">Your name *</span>
      <input className={enquiryInputCls} value={form.name}
        onChange={(e) => setField("name", e.target.value)} data-testid="enquiry-name" />
    </label>
    <label className="block">
      <span className="font-semibold text-forest">Phone number</span>
      <input className={enquiryInputCls} value={form.phone} inputMode="tel"
        onChange={(e) => setField("phone", e.target.value)} data-testid="enquiry-phone" />
    </label>
    <label className="block">
      <span className="font-semibold text-forest">Email address *</span>
      <input type="email" className={enquiryInputCls} value={form.email}
        onChange={(e) => setField("email", e.target.value)} data-testid="enquiry-email" />
    </label>
    <label className="block">
      <span className="font-semibold text-forest">What do you need help with? *</span>
      <textarea rows={4} className="mt-1 w-full p-4 rounded-2xl border-2 border-forest/15 bg-white text-lg focus:outline-none focus:border-forest"
        value={form.help_with} onChange={(e) => setField("help_with", e.target.value)}
        data-testid="enquiry-help-with"
        placeholder="e.g. I'd like help with a Council Tax Reduction form." />
    </label>
    <EnquiryToggleGroup
      label="Is this for you or someone else?"
      value={form.for_whom}
      options={ENQUIRY_FORWHOM}
      onChange={(v) => setField("for_whom", v)}
      testIdPrefix="enquiry-forwhom"
    />
    <EnquiryToggleGroup
      label="Preferred contact method"
      value={form.preferred_contact}
      options={ENQUIRY_CONTACT}
      onChange={(v) => setField("preferred_contact", v)}
      testIdPrefix="enquiry-contact"
      capitalize
    />
    <button type="submit" disabled={submitting} className="btn-primary mt-3" data-testid="enquiry-submit">
      {submitting ? "Sending…" : "Send Enquiry"}
    </button>
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(ENQUIRY_INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!isEnquiryComplete(form)) {
      toast.error("Please fill in your name, email and what you need help with.");
      return;
    }
    setSubmitting(true);
    try {
      await createEnquiry(form);
      toast.success("Thanks — Sarah will be in touch soon.");
      setDone(true);
    } catch {
      toast.error("Could not send your message. Please try calling Sarah.");
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  return (
    <Section id="enquiry" testId="section-contact-form" className="bg-sage/20">
      <Toaster richColors position="top-center" />
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <ContactFormIntro />
        <form onSubmit={submit} className="soft-card p-6 sm:p-9 relative overflow-hidden" data-testid="contact-form">
          <div className="yellow-square w-24 h-24 -top-4 -right-4" />
          {done ? <EnquiryConfirmation /> : <EnquiryFields form={form} setField={setField} submitting={submitting} />}
        </form>
      </div>
    </Section>
  );
};

/* -------------------------------- PAGE-SPECIFIC SECTIONS -------------------------------- */
export const ServicesHero = () => (
  <Section id="services-hero" testId="section-services-hero" className="pt-20 pb-10">
    <div className="max-w-3xl">
      <span className="chip">Services</span>
      <h1 className="section-title mt-4">Admin, applications and everyday help</h1>
      <p className="section-sub mt-4">All the practical things Sarah can take off your plate — gently, calmly and clearly.</p>
      <div className="mt-7"><BookButton testId="services-hero-book" /></div>
    </div>
  </Section>
);

export const ServiceCards = () => {
  const cards = [
    { title: "Benefit Application Support", text: "Help with the practical side of benefit applications — gathering information, sorting documents, completing forms and explaining what happens next.", icon: FileText, examples: ["Attendance Allowance", "Pension Credit", "Blue Badge", "Council Tax Reduction", "PIP-related paperwork", "Local authority forms"] },
    { title: "Household Admin", text: "Everyday paperwork and household routines — letters, bills, emails, appointments, online accounts, filing, reminders and chasing updates.", icon: ClipboardList },
    { title: "Support for Elderly Clients", text: "Patient, respectful help for older people who may find forms, websites, letters or official applications stressful or confusing.", icon: Heart },
    { title: "Family Support", text: "Support for families helping parents, grandparents or vulnerable relatives but who don't have time to manage everything themselves.", icon: Users },
    { title: "Home Services & Everyday Help", text: "Practical support arranging appointments, contacting providers, sorting household tasks and helping things move forward.", icon: HomeIcon },
    { title: "Applications & Forms", text: "Support with online and paper applications, renewals, registrations, official letters and general life admin.", icon: NotebookPen },
  ];
  return (
    <Section id="service-cards" testId="section-service-cards">
      <div className="grid lg:grid-cols-2 gap-6">
        {cards.map((c, i) => (
          <article key={c.title} className="soft-card p-8" data-testid={`detailed-service-${i}`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-daisy/40 grid place-items-center shrink-0">
                <c.icon className="w-7 h-7 text-forest" strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-serif text-2xl text-forest">{c.title}</h3>
                <p className="mt-2 text-charcoal/85">{c.text}</p>
                {c.examples && (
                  <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
                    {c.examples.map((e) => (
                      <li key={e} className="flex items-start gap-2 text-base">
                        <BadgeCheck className="w-4 h-4 text-forest mt-1 shrink-0" /> {e}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
};

export const BenefitHero = () => (
  <Section id="benefit-hero" testId="section-benefit-hero" className="pt-20 pb-10">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="chip"><FileText className="w-4 h-4" /> Benefit Application Help</span>
        <h1 className="section-title mt-4">Benefit application support — calmly, carefully, clearly.</h1>
        <p className="section-sub mt-4">
          Sarah helps with the practical side of benefit applications. From gathering information to completing forms and
          explaining what happens next — without the stress.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <BookButton testId="benefit-hero-book">Book a Benefits Help Call</BookButton>
          <a href={SITE.tel} className="btn-ghost" data-testid="benefit-hero-call"><Phone className="w-5 h-5" /> Call Sarah</a>
        </div>
      </div>
      <div className="relative">
        <div className="yellow-square w-2/3 h-2/3 -top-4 -right-4" />
        <img src={PAPERS_IMG} alt="Forms being filled in calmly" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" />
      </div>
    </div>
  </Section>
);

export const BenefitTypes = () => {
  const items = ["Attendance Allowance", "Pension Credit", "Blue Badge applications", "Council Tax Reduction", "PIP-related paperwork", "Carer's Allowance enquiries", "Housing Benefit forms", "Other local authority forms"];
  return (
    <Section id="benefit-types" testId="section-benefit-types" className="bg-white">
      <h2 className="section-title">Common applications Sarah helps with</h2>
      <p className="section-sub mt-4">If yours isn't listed, just ask. Most forms follow a similar pattern.</p>
      <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((x, i) => (
          <li key={x} className="soft-card p-5 flex items-center gap-3" data-testid={`benefit-type-${i}`}>
            <BadgeCheck className="w-6 h-6 text-forest shrink-0" />
            <span className="font-semibold text-lg text-forest">{x}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
};

export const HowSarahHelps = () => {
  const items = [
    "Understands what the form is asking",
    "Helps gather the right information",
    "Completes the form clearly and carefully",
    "Prepares supporting documents",
    "Explains what happens next, step by step",
    "Follows up where appropriate",
  ];
  return (
    <Section id="how-sarah-helps" testId="section-how-sarah-helps">
      <h2 className="section-title">How Sarah helps</h2>
      <p className="section-sub mt-4">No jargon. No pressure. Just practical help with the bits that feel hardest.</p>
      <ul className="mt-10 grid md:grid-cols-2 gap-4">
        {items.map((x, i) => (
          <li key={x} className="flex items-start gap-3 soft-card p-5" data-testid={`how-help-${i}`}>
            <CheckCircle2 className="w-6 h-6 text-forest mt-1" />
            <span className="text-lg">{x}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
};

export const DocumentsNeeded = () => {
  const docs = ["Proof of identity", "Proof of address", "Recent medical letters (where relevant)", "Bank or income details (where relevant)", "Existing benefit letters", "Previous form copies"];
  return (
    <Section id="documents-needed" testId="section-documents-needed" className="bg-sage/25">
      <h2 className="section-title">Helpful documents to gather</h2>
      <p className="section-sub mt-4">Don't worry if you don't have everything — Sarah will help work out what is needed.</p>
      <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((d, i) => (
          <li key={d} className="soft-card p-5 flex items-center gap-3 bg-cream" data-testid={`doc-${i}`}>
            <FileText className="w-6 h-6 text-forest shrink-0" />
            <span className="text-lg">{d}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
};

export const BenefitProcess = () => {
  const steps = [
    "Free call to understand what you need",
    "Gather the right information together",
    "Complete the form carefully with you",
    "Prepare supporting documents",
    "Submit and explain the next steps",
  ];
  return (
    <Section id="benefit-process" testId="section-benefit-process">
      <h2 className="section-title">The process, step by step</h2>
      <ol className="mt-10 grid md:grid-cols-5 gap-4">
        {steps.map((s, i) => (
          <li key={s} className="soft-card p-5 relative" data-testid={`benefit-step-${i}`}>
            <span className="absolute -top-3 left-5 w-8 h-8 grid place-items-center rounded-full bg-daisy text-forest font-bold">{i + 1}</span>
            <p className="mt-2 text-base">{s}</p>
          </li>
        ))}
      </ol>
    </Section>
  );
};

export const ElderlyHero = () => (
  <Section id="elderly-hero" testId="section-elderly-hero" className="pt-20 pb-10">
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="chip"><Heart className="w-4 h-4" /> Support for Elderly People</span>
        <h1 className="section-title mt-4">A kind, patient extra pair of hands.</h1>
        <p className="section-sub mt-4">
          Help with forms, official letters, online accounts and household admin — for older people, and the families who help them.
        </p>
        <div className="mt-7"><BookButton testId="elderly-hero-book">Speak to Sarah</BookButton></div>
      </div>
      <div className="relative">
        <div className="yellow-square w-2/3 h-2/3 -top-6 -right-6" />
        <img src={SERVICE_IMG} alt="A friendly conversation" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" />
      </div>
    </div>
  </Section>
);

export const ElderlySupportBlock = () => (
  <Section id="elderly-support" testId="section-elderly-support" className="bg-white">
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
      <div>
        <h2 className="section-title">Patient, respectful help for older people</h2>
        <p className="section-sub mt-4">
          Forms, websites and official letters can feel stressful. Sarah explains everything in plain English, at a pace that
          suits you — and helps get things sorted.
        </p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-3">
          {["Reading and explaining letters", "Filling in paper or online forms", "Setting up online accounts", "Arranging appointments", "Sorting documents", "Following up with providers"].map((x) => (
            <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-forest mt-1" /> {x}</li>
          ))}
        </ul>
      </div>
      <div className="soft-card p-7 bg-cream">
        <h3 className="font-serif text-2xl text-forest">A note for families</h3>
        <p className="mt-3 text-charcoal/85">
          If you're helping a parent or relative from a distance, Sarah can act as the calm, methodical person on the ground —
          keeping you in the loop and reducing the day-to-day worry.
        </p>
        <BookButton testId="elderly-family-book" /></div>
    </div>
  </Section>
);

export const FamilySupportBlock = () => (
  <Section id="family-support" testId="section-family-support">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div className="relative">
        <div className="yellow-square w-1/2 h-3/5 -top-4 -left-4" />
        <img src={ABOUT_IMG} alt="Family support" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" />
      </div>
      <div>
        <h2 className="section-title">Family support</h2>
        <p className="section-sub mt-4">
          When you're juggling work, kids and helping a parent, the admin can feel relentless. Sarah quietly takes
          things off your plate so you can focus on the human side.
        </p>
        <ul className="mt-6 space-y-3">
          {["Help arranging Attendance Allowance or Pension Credit", "Sorting through difficult letters", "Co-ordinating appointments", "Untangling household admin"].map((x) => (
            <li key={x} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-forest mt-1" /> {x}</li>
          ))}
        </ul>
        <div className="mt-6"><BookButton testId="family-support-book" /></div>
      </div>
    </div>
  </Section>
);

export const HouseholdAdminBlock = () => (
  <Section id="household-admin" testId="section-household-admin" className="bg-white">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <h2 className="section-title">Household Admin</h2>
        <p className="section-sub mt-4">
          The pile of letters, the bills you've been meaning to sort, the online accounts you can't get into — Sarah can
          quietly work through it with you.
        </p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-3">
          {["Bills and direct debits", "Letters and admin filing", "Email and inbox triage", "Online account setup", "Appointment scheduling", "Chasing providers"].map((x) => (
            <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-forest mt-1" /> {x}</li>
          ))}
        </ul>
      </div>
      <div className="relative">
        <div className="yellow-square w-3/5 h-3/5 -bottom-4 -right-4" />
        <img src={PAPERS_IMG} alt="Household paperwork sorted" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" />
      </div>
    </div>
  </Section>
);

export const ApplicationsFormsBlock = () => (
  <Section id="applications-forms" testId="section-applications-forms">
    <h2 className="section-title">Applications & Forms</h2>
    <p className="section-sub mt-4">
      Online and paper applications, renewals, registrations and official letters. Sarah keeps it calm and clear from start
      to finish.
    </p>
    <ul className="mt-8 grid md:grid-cols-3 gap-4">
      {["Online forms", "Paper forms", "Renewals", "Registrations", "Official letter replies", "Local authority paperwork"].map((x) => (
        <li key={x} className="soft-card p-5 flex items-center gap-3"><BadgeCheck className="w-6 h-6 text-forest" /> <span className="text-lg">{x}</span></li>
      ))}
    </ul>
  </Section>
);

export const HomeServicesBlock = () => (
  <Section id="home-services" testId="section-home-services" className="bg-sage/20">
    <h2 className="section-title">Home Services & Everyday Help</h2>
    <p className="section-sub mt-4">
      Sometimes you just need someone calmly capable to make the calls, send the emails and book the things. Sarah quietly gets
      it done.
    </p>
    <div className="mt-8 grid md:grid-cols-3 gap-4">
      {["Booking appointments", "Speaking to providers", "Following up enquiries", "Sorting paperwork", "Setting reminders", "Co-ordinating tasks"].map((x) => (
        <div key={x} className="soft-card p-5 flex items-center gap-3 bg-cream"><HandHelping className="w-6 h-6 text-forest" /> <span className="text-lg">{x}</span></div>
      ))}
    </div>
  </Section>
);

export const AboutHero = () => (
  <Section id="about-hero" testId="section-about-hero" className="pt-20 pb-10">
    <div className="max-w-3xl">
      <span className="chip"><Coffee className="w-4 h-4" /> About</span>
      <h1 className="section-title mt-4">Meet Sarah — the friendly, calmly capable one.</h1>
      <p className="section-sub mt-4">
        Daisy Does was born from a simple idea: that everyone deserves calm, kind help with the admin side of life.
      </p>
    </div>
  </Section>
);

export const Values = () => {
  const values = [
    { title: "Kind", text: "We meet people where they are. No judgement, ever." },
    { title: "Capable", text: "Methodical, thorough and reliable with the practical work." },
    { title: "Clear", text: "Plain English. No jargon. No surprises." },
    { title: "Discreet", text: "Your documents, situation and family stay private." },
  ];
  return (
    <Section id="values" testId="section-values">
      <h2 className="section-title">What we stand for</h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {values.map((v, i) => (
          <article key={v.title} className="soft-card p-7" data-testid={`value-${i}`}>
            <h3 className="font-serif text-2xl text-forest">{v.title}</h3>
            <p className="mt-3 text-charcoal/85">{v.text}</p>
          </article>
        ))}
      </div>
    </Section>
  );
};

export const WhyStarted = () => (
  <Section id="why-started" testId="section-why-started" className="bg-white">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div className="relative">
        <div className="yellow-square w-2/3 h-2/3 -top-4 -left-4" />
        <img src={ABOUT_IMG} alt="Sarah" className="relative rounded-3xl shadow-xl w-full object-cover aspect-[5/4]" />
      </div>
      <div>
        <h2 className="section-title">Why I started Daisy Does</h2>
        <p className="section-sub mt-4">
          So many people quietly struggle with paperwork — and it doesn't have to be that way. After helping family members
          with forms, applications and admin, I realised how much calmer life feels when someone methodical steps in.
        </p>
        <p className="mt-4 text-lg text-charcoal/85">
          Daisy Does is for anyone who needs a kind, capable extra pair of hands.
        </p>
        <div className="mt-6"><BookButton testId="why-started-book">Speak to Sarah</BookButton></div>
      </div>
    </div>
  </Section>
);

export const PricingHero = () => (
  <Section id="pricing-hero" testId="section-pricing-hero" className="pt-20 pb-10">
    <div className="max-w-3xl">
      <span className="chip">Pricing</span>
      <h1 className="section-title mt-4">Simple, clear pricing.</h1>
      <p className="section-sub mt-4">Start with a free call. Sarah will then suggest the right option for what you need.</p>
    </div>
  </Section>
);

export const PricingCards = () => {
  const cards = [
    { title: "Introductory Call", price: "Free", description: "15 minutes to understand what help is needed.", highlight: true, features: ["No obligation", "For you or someone you help", "Simple next steps explained"] },
    { title: "Hourly Support", price: "£45 / hour", description: "Flexible help with admin, applications and household tasks.", features: ["Pay only for the time you need", "Use ad-hoc or regular", "Plain-English support"] },
    { title: "Application Support", price: "Fixed fee", description: "By agreement, for a defined application or form.", features: ["Quote agreed up front", "Includes preparation & submission", "Documents sorted neatly"] },
    { title: "Home Admin Session", price: "Half / full day", description: "For bigger admin or household sorting work.", features: ["Tackle the pile in one go", "Calm, methodical approach", "Clear handover at the end"] },
  ];
  return (
    <Section id="pricing-cards" testId="section-pricing-cards">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c, i) => (
          <div key={c.title} data-testid={`pricing-detail-${i}`} className={`soft-card p-7 relative flex flex-col ${c.highlight ? "ring-2 ring-daisy" : ""}`}>
            {c.highlight && <span className="absolute -top-3 left-7 chip bg-daisy">Best place to start</span>}
            <h3 className="font-serif text-2xl text-forest">{c.title}</h3>
            <p className="mt-3 text-3xl font-bold text-forest">{c.price}</p>
            <p className="mt-3 text-charcoal/85">{c.description}</p>
            <ul className="mt-4 space-y-2 flex-1">
              {c.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-base"><CheckCircle2 className="w-5 h-5 text-forest mt-1" /> {f}</li>
              ))}
            </ul>
            <Link to="/contact#booking" className="mt-6 btn-primary h-12 text-base" data-testid={`pricing-detail-book-${i}`}>
              <Calendar className="w-5 h-5" /> Book a Free Call
            </Link>
          </div>
        ))}
      </div>
    </Section>
  );
};

export const PackageOptions = () => (
  <Section id="package-options" testId="section-package-options" className="bg-white">
    <h2 className="section-title">Bespoke packages</h2>
    <p className="section-sub mt-4">
      Bigger task? Sarah can quote a fixed package — perfect for families wanting clarity on cost up front.
    </p>
    <ul className="mt-8 grid md:grid-cols-3 gap-4">
      {["Attendance Allowance application", "Pension Credit application", "Blue Badge application", "Council Tax Reduction", "Major home admin clear-up", "On-going monthly support"].map((x) => (
        <li key={x} className="soft-card p-5 flex items-center gap-3"><BadgeCheck className="w-6 h-6 text-forest" /> <span className="text-lg">{x}</span></li>
      ))}
    </ul>
  </Section>
);

export const PricingNote = () => (
  <Section id="pricing-note" testId="section-pricing-note">
    <div className="soft-card p-7 bg-cream border-2 border-daisy/40 max-w-3xl">
      <p className="text-lg text-charcoal/85">
        Prices are guides — the free call helps Sarah work out exactly what you need. There's no obligation, and no
        commitment until you're happy.
      </p>
    </div>
  </Section>
);

export const ContactHero = () => (
  <Section id="contact-hero" testId="section-contact-hero" className="pt-20 pb-10">
    <div className="max-w-3xl">
      <span className="chip"><Mail className="w-4 h-4" /> Contact</span>
      <h1 className="section-title mt-4">Speak to Sarah</h1>
      <p className="section-sub mt-4">Book a free call, send a message or call directly. Whatever feels easiest.</p>
    </div>
  </Section>
);

export const ContactReassurance = () => (
  <Section id="contact-reassurance" testId="section-contact-reassurance" className="bg-white py-12">
    <div className="grid md:grid-cols-3 gap-4 max-w-4xl">
      {["No pressure, ever.", "For you, a parent, grandparent or someone you help.", "Sarah will explain the next step clearly."].map((x, i) => (
        <div key={x} className="soft-card p-5 flex items-start gap-3 bg-cream" data-testid={`reassure-${i}`}>
          <Heart className="w-6 h-6 text-forest shrink-0" />
          <p className="text-base">{x}</p>
        </div>
      ))}
    </div>
  </Section>
);
